import React, {
  memo,
  useEffect,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Paper, CircularProgress } from '@mui/material';
import { isMobile } from 'react-device-detect';
import { translation } from 'utils/translate';
import { getLawUrl } from 'utils/commonTools';
import apiService from 'services/apiService';
import './LawUpdatesBlock.css';

const sortByLawId = (a, b) => {
  // 159/2022 -> [159, 2022] and convert both to numbers
  const [aId, aYear] = a.law_id.split('/').map(Number);
  const [bId, bYear] = b.law_id.split('/').map(Number);

  if (aYear === bYear) return aId - bId;
  return aYear - bYear;
};

const emptyLaws = {
  // { title: string, law_id: string, combined_title: string }[],
  new_laws: [],
  substitutions: [],
};

const LawUpdatesBlock = memo(({ laws, lang }) => {
  const [blockLaws, setBlockLaws] = useState(emptyLaws);

  // generate a memoized list of related law ids which are used
  // to fetch data from backend
  const lawIds = useMemo(() => {
    let _ids = laws
      .map((law) => law.laki_id)
      .filter((id) => id); // remove undefineds
    // convert to a set and back to remove duplicates
    // (swe and fin versions are both often present in TimeLine and share the same id)
    _ids = [...new Set(_ids)];
    return _ids;
  }, [laws]);

  // fetch law data from backend
  useEffect(() => {
    const fetchLaws = async (ids, _lang) => {
      try {
        const res = await apiService.fetchData(
          `lawupdates?ids=${ids.join(',')}&lang=${_lang}`,
          'json',
        );
        setBlockLaws(res.response);
      } catch (error) {
        setBlockLaws(emptyLaws);
      }
    };

    if (!lawIds.length) return;
    fetchLaws(lawIds, lang);
  }, [lawIds, lang]);

  // sort laws whenever new data is fetched
  const sortedLaws = useMemo(() => {
    const {
      new_laws: newLaws,
      substitutions,
    } = blockLaws;
    return {
      new_laws: newLaws.sort(sortByLawId),
      substitutions: substitutions.sort(sortByLawId),
    };
  }, [blockLaws]);

  // do not render the block if no laws are available
  if (
    !lawIds.length &&
    !sortedLaws.new_laws.length &&
    !sortedLaws.substitutions.length
  )
    return null;

  // if there are laws but they haven't been loaded (ids exist but state is empty)
  // render a loading circle
  if (
    lawIds.length > 0 &&
    !sortedLaws.new_laws.length &&
    !sortedLaws.substitutions.length
  )
    return (
      <div className="progress-wrapper fade-in">
        <CircularProgress size={68} />
      </div>
    );

  return (
    <Paper
      elevation={0}
      className={
        isMobile ? 'law-updates-paper-mobile' : null
      }
    >
      <div className="block block--law-updates">
        <h4>
          {translation(
            'law_updates',
            'project_statutes',
            lang,
          )}
        </h4>

        <LawUpdateList
          lawList={sortedLaws.new_laws}
          title={translation(
            'law_updates',
            'new_laws',
            lang,
          )}
        />
        <LawUpdateList
          lawList={sortedLaws.substitutions}
          title={translation(
            'law_updates',
            'substitutions',
            lang,
          )}
        />
      </div>
    </Paper>
  );
});

LawUpdatesBlock.propTypes = {
  laws: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  lang: PropTypes.string.isRequired,
};

const LawUpdateList = memo(({ title, lawList }) => {
  if (!lawList.length) return null;
  return (
    <div className="law-update-list">
      <p className="block--subtitle">{title}</p>
      {lawList.map((law) => (
        <LawLink key={law.law_id} law={law} />
      ))}
    </div>
  );
});

LawUpdateList.propTypes = {
  title: PropTypes.string.isRequired,
  lawList: PropTypes.arrayOf(PropTypes.shape({}))
    .isRequired,
};

const LawLink = memo(({ law }) => {
  // memoize to avoid running on unrelated renders (e.g. when window resized)
  const { title, law_id: lawId, combined } = law;
  if (!combined && !title) return null;

  const link = getLawUrl(lawId);
  if (!link) return <p>{title}</p>;

  return (
    <p>
      <a
        href={link}
        rel="noopener noreferrer"
        target="_blank"
      >
        {combined}
      </a>
    </p>
  );
});

LawLink.propTypes = {
  law: PropTypes.shape({
    title: PropTypes.string.isRequired,
    law_id: PropTypes.string.isRequired,
    combined: PropTypes.string.isRequired,
  }).isRequired,
};

export default LawUpdatesBlock;
